import { type VueHeadClient } from '@unhead/vue'
import type { Router } from 'vue-router'
import {
    createRouter,
    ROUTE_BLOG,
    ROUTE_BLOG_AUTHOR,
    ROUTE_BLOG_CATEGORY,
    ROUTE_BLOG_POST,
    ROUTE_BLOG_TAG,
    ROUTE_DTL_REPORT,
    ROUTE_ENGAGEMENT_RING,
    ROUTE_ENGAGEMENT_RING_GEMSTONE,
    ROUTE_ENGAGEMENT_RING_LAB_GROWN,
    ROUTE_ENGAGEMENT_RING_STONE_RECOMMENDATIONS,
    ROUTE_ENGAGEMENT_RINGS,
    ROUTE_ENGAGEMENT_RINGS_HOME,
    ROUTE_GEMSTONE_SEARCH,
    ROUTE_HOME,
    ROUTE_JEWELLERIES,
    ROUTE_JEWELLERY,
    ROUTE_LAB_GROWN_DIAMOND_SEARCH,
    ROUTE_NATURAL_DIAMOND_SEARCH,
    ROUTE_NON_SKU_LAB_GROWN_DIAMOND_SEARCH,
    ROUTE_PROFILE_MY_ORDERS,
    ROUTE_QUESTIONNAIRE,
    ROUTE_READY_TO_GO_RING,
    ROUTE_READY_TO_GO_RINGS,
    ROUTE_WEDDING_RING,
    ROUTE_WEDDING_RINGS,
    ROUTE_WEDDING_RINGS_HOME,
} from './router'
import {
    type App as VueApp,
    type ComponentPublicInstance,
    type DirectiveBinding,
    type Ref,
} from 'vue'
import { logVueError } from './helpers/error'

import {
    type Client as GraphqlClient,
    createClient,
    fetchExchange,
    install,
    mapExchange,
    ssrExchange as createSSRExchange,
} from '@urql/vue'
import { addHeaderExchange } from '@/graphql/urql/exchange'
import { persistedExchange } from '@urql/exchange-persisted'
import { type AppI18nPlugin, createI18nPlugin } from '@/i18n'
import { getRegionFromUrl, installRegionPlugin } from '@/plugins/region'

import { installCurrencyPlugin } from '@/plugins/currency'
import { createPinia, type Pinia } from 'pinia'
import { createPersistedState } from 'pinia-plugin-persistedstate'
import {
    installMetaRobotsPlugin,
    type MetaRobotsPlugin,
} from '@/plugins/robots'
import { installMenuPlugin } from '@/plugins/menu'
import { installRegistryPlugin } from '@/helpers/registry'
import { createEmptyStorage } from '@/stores'
import { installUserAgent } from '@/plugins/user-agent'
import { tooltipDirective } from './directives/tooltip/tooltip'
import { useGtmVar } from './composables/app/gtm/useGtmVar'
import {
    installReleasedUrls,
    type ReleasedUrlsChecker,
} from './plugins/released-urls/released-urls'
import { getWindow } from '@/helpers/globals'
import { provideGoogleSignInId } from './plugins/google-sign-in-id'
import { providefacebookAppId } from '@/plugins/facebook-sign-in-id'
import { installRoutesPlugin, type Routes } from '@/router/routes'
import {
    type AbsoluteUrlGenerator,
    installAbsoluteUrl,
} from '@/composables/app/useAbsoluteUrl'
import { installCurrentUrl } from '@/plugins/current-url.ts'
import { installCookieStore } from '@/plugins/cookie-store.ts'
import { installMoneyFormatter } from '@/plugins/money-formatter.ts'
import { installCurrencyInputPlugin } from '@/plugins/currency-input.ts'
import { installRouterNavigation } from '@/plugins/released-urls/useRouterNavigation.ts'
import {
    createObjectCookie,
    installReactiveCookiePlugin,
    makeCookie,
} from '@/plugins/cookie-plugin'
import { installRingBuilderStore } from '@/plugins/ring-builder-store.ts'
import { trackGtmEvent } from '@/composables/app/gtm/event.ts'
import { installIOS } from '@/plugins/is-ios.ts'
import { installAuth } from '@/plugins/auth.ts'
import {
    createClientRedirect,
    createServerRedirect,
    provideRedirect,
    type RedirectFunction,
} from '@/ssr/plugins/redirect.ts'
import { installStatusPlugin } from '@/ssr/plugins/status.ts'
import type { InitialState, SSRExchange } from '@/initial-state.ts'
import type { AppSentry } from '@/sentry.ts'
import { installGraphqlClient } from '@/plugins/graphql-client.ts'
import { installAffirm } from '@/third-party/affirm.ts'
import { provideMsClarityId } from '@/third-party/clarity.ts'
import { installSegment } from '@/third-party/segment.ts'
import { installIntercom } from '@/third-party/intercom.ts'
import { provideCheckoutComApiUrl } from '@/third-party/checkout-com.ts'
import {
    importFouanalytics,
    installFouanalyticsConfig,
} from '@/third-party/fouanalytics.ts'
import {
    installRingBuilderDataStore,
    type RingBuilderDataStore,
} from '@/plugins/ring-builder-data-store.ts'
import { installInitialState } from '@/plugins/initial-state.ts'
import { installApp, installFoApp } from '@/plugins/app.ts'
import { importDelayedScript } from '@/third-party/helpers'
import { installIsMobileAgent } from '@/plugins/is-mobile-agent.ts'
import { headerMobileBreakpoint, installIsMobile } from '@/plugins/is-mobile.ts'
import { installBench } from '@/plugins/bench.ts'
import { installEventBus } from '@/plugins/event-bus.ts'
import {
    installIntercomLauncher,
    type IntercomCookieVariables,
} from '@/plugins/intercom/launcher.ts'
import { retryExchange } from '@urql/exchange-retry'
import { isRateLimitError } from '@/helpers/errors'
import { installIskWorkingHoursUkPlugin } from '@/plugins/business/working-hours'
import { installIsBot } from '@/plugins/is-bot.ts'
// @ts-expect-error no declarations available
import withResolvers from 'promise.withresolvers'
import { installCountryPlugin } from '@/plugins/country.ts'
import { type SentryConfig } from '@/app-config.ts'
import type { BootstrapConfig } from '@/bootstrap-config.ts'
import { installBenchAggregated } from '@/plugins/bench2.ts'
import type { GraphQLError } from 'graphql/index'

export interface FrontOfficeApp {
    app: VueApp
    graphqlClient: Ref<GraphqlClient>
    router: Router
    head: VueHeadClient
    i18n: AppI18nPlugin
    locale: Ref<string>
    ssrExchange: SSRExchange
    absoluteUrlGenerator: AbsoluteUrlGenerator
    isReleasedUrl: ReleasedUrlsChecker
    // piniaStorage: PiniaStoragePlugin
    pinia: Pinia
    ringBuilderDataStore: RingBuilderDataStore
    routes: Routes
    abortController: AbortController
    // cookieHeader: { cookie: string }
    // entry-client.ts-specific
    // headObject: ComputedRef<HeadObjectPlain>
    // headUpdatesAllowed: ComputedRef<boolean>
    robots: MetaRobotsPlugin
    initialUrl: URL
}

withResolvers.shim()

export function createInitialState(): InitialState {
    return {
        api: {},
        pinia: {},
        rb: null,
    }
}

export function createApp(
    app: VueApp,
    {
        history,
        initialState,
        urls,
        msClarityId,
        checkoutComApiUrl,
        sentry: sentryConfig,
        initialUrl,
        userAgent,
        meta,
        cookieProvider,
        analyticsId,
        googleSignInId,
        facebookAppId,
        intercom,
        affirmUS,
        affirmGB,
        fetch,
        exchanges,
        qaIsNoJs,
        fouanalytics,
        createHead,
        redirectState,
        statusState,
    }: BootstrapConfig
): FrontOfficeApp {
    provideGoogleSignInId(app, googleSignInId)
    providefacebookAppId(app, facebookAppId)

    app.config.errorHandler = function (
        err: unknown,
        vm: ComponentPublicInstance | null,
        info: string
    ) {
        logVueError(err, vm, info)
    }

    app.config.performance = import.meta.env.DEV

    let sentry: AppSentry | null = null
    let sentryPromise: Promise<AppSentry> | undefined = undefined

    async function importSentry(): Promise<AppSentry | undefined> {
        if (!sentryConfig) {
            return undefined
        }

        if (sentry) {
            return sentry
        }

        if (!sentryPromise) {
            sentryPromise = doImportSentry(sentryConfig)
        }

        sentry = await sentryPromise

        return sentry
    }

    async function doImportSentry(config: SentryConfig): Promise<AppSentry> {
        const sentry: AppSentry = (await import('@/sentry.ts')).Sentry

        sentry.init({
            app,
            dsn: config.dsn,
            environment: config.environment || 'staging',
            integrations: [
                sentry.replayIntegration({
                    unblock: ['.sentry-unblock, [data-sentry-unblock]'],
                    unmask: ['.sentry-unmask, [data-sentry-unmask]'],
                }),
                sentry.captureConsoleIntegration({
                    // stupid sentry, no union but strings, dafuq => ['log', 'info', 'warn', 'error', 'debug', 'assert']
                    levels: ['error'],
                }),
            ],
            tracesSampleRate: 0.01,
            tracePropagationTargets: [urls.api],
            replaysSessionSampleRate: 0,
            replaysOnErrorSampleRate: 1.0,
            denyUrls: [/chrome-extension:\/\//],
            ignoreErrors: [
                'TypeError: Failed to fetch',
                'TypeError: NetworkError when attempting to fetch resource.',
                'TypeError: cancelled',
                /Importing a module script failed/,
                /Failed to fetch dynamically imported module/,
                'TypeError: Load failed',
                /Unable to preload CSS/,
            ],
        })

        return sentry
    }

    const sentryEnabled = Boolean(sentryConfig)

    if (sentryEnabled && !import.meta.env.SSR) {
        importDelayedScript(importSentry)
    }

    provideMsClarityId(app, msClarityId)
    provideCheckoutComApiUrl(app, checkoutComApiUrl)

    installFouanalyticsConfig(app, fouanalytics)

    if (fouanalytics.enabled) {
        importDelayedScript(importFouanalytics)
    }

    installInitialState(app, initialState)

    // IF CLIENT APPLIES INITIAL STATE TO PINIA

    const head = createHead()
    app.use(head)

    const pinia = createPinia()
    if (!import.meta.env.SSR) {
        // Let it default to localStorage
        pinia.use(createPersistedState())
    } else {
        // Use empty storage by default
        // Stores that will persist via cookies will pass their own storage instance
        pinia.use(
            createPersistedState({
                storage: createEmptyStorage(),
            })
        )
    }
    app.use(pinia)

    installUserAgent(app, userAgent)
    const isMobileAgentPlugin = installIsMobileAgent(app, userAgent)
    const isMobilePlugin = installIsMobile(app, isMobileAgentPlugin)

    const isMobile = isMobilePlugin.createIsMobile(headerMobileBreakpoint)
    const router = createRouter(history, isMobile)
    installCurrentUrl(app, router, initialUrl)

    if (!statusState) {
        throw new Error('No status state provided')
    }
    installStatusPlugin(app, statusState)

    let redirect: RedirectFunction
    if (import.meta.env.SSR) {
        if (!redirectState) {
            throw new Error('No redirect state provided')
        }
        redirect = createServerRedirect(redirectState)
    } else {
        redirect = createClientRedirect(async (location, status) => {
            const redirect = status === 301 ? router.replace : router.push

            await redirect(location)
                // eslint-disable-next-line no-console
                .catch(console.warn)
        })
    }

    provideRedirect(app, redirect)

    const absoluteUrlGenerator = installAbsoluteUrl(
        app,
        initialUrl.host,
        initialUrl.protocol,
        router
    )

    const window = getWindow()

    if (window) {
        router.onError((error, to) => {
            if (
                error.message.includes(
                    'Failed to fetch dynamically imported module'
                ) ||
                error.message.includes('Importing a module script failed')
            ) {
                if (!to?.fullPath) {
                    window.location.reload()
                } else {
                    window.location = to.fullPath
                }
            }
        })
    }

    app.use(router)

    const appTranslator = createI18nPlugin(app, {
        defaultLocale: 'en-gb',
        fallbackLocale: 'en-gb',
    })
    const { i18n, locale, setLocale } = appTranslator
    app.use(i18n)

    app.config.globalProperties['$t'] = i18n.global.t

    if (!import.meta.env.SSR) {
        pinia.state.value = initialState.pinia || {}
    }

    app.directive('tooltip', tooltipDirective)

    const cookieStore = installCookieStore(app)
    const cookies = installReactiveCookiePlugin(
        app,
        cookieProvider,
        cookieStore
    )

    const region = getRegionFromUrl(initialUrl)
    const regionPlugin = installRegionPlugin(app, {
        initial: region,
        router,
        redirect,
    })

    const countryPlugin = installCountryPlugin(
        app,
        regionPlugin,
        setLocale,
        cookies,
        redirect,
        router
    )

    const currencyPlugin = installCurrencyPlugin(app, {
        locale,
        cookies,
        countryPlugin,
    })

    const authPlugin = installAuth(app)
    installAffirm(app, countryPlugin.countryCode, affirmUS, affirmGB)
    // const workingHoursPlugin = installIskWorkingHoursUkPlugin(app)
    installIskWorkingHoursUkPlugin(app)

    const intercomPlugin = installIntercom(
        app,
        cookies,
        authPlugin,
        intercom?.app_id
    )

    const intercomVariablesCookie = createObjectCookie<IntercomCookieVariables>(
        'taylorandhart_intercom',
        undefined,
        cookies
    )
    installIntercomLauncher(
        app,
        intercomPlugin,
        authPlugin,
        router,
        intercomVariablesCookie
        // regionPlugin.regionCode,
        // workingHoursPlugin.isWorkingHoursUk
    )

    installMenuPlugin(app)
    installRegistryPlugin(app)
    installIOS(app, userAgent)

    const routes = installRoutesPlugin(app, regionPlugin)

    const robots = installMetaRobotsPlugin(app, {
        default: {
            index: meta?.index || true,
            follow: meta?.follow || true,
        },
    })

    installCurrencyInputPlugin(app, countryPlugin, currencyPlugin)

    installMoneyFormatter(app, appTranslator)

    // installPageMeta(
    //     app,
    //     computed(() => {
    //         return {
    //             title: i18n.global.t('globals.default_meta_title'),
    //             description: i18n.global.t('globals.default_meta_description'),
    //         }
    //     })
    // )

    // const { updatesAllowed } = pageMeta

    // const headObject: ComputedRef<HeadObjectPlain> = computed(() => {
    //     return {
    //         htmlAttrs: {
    //             lang: locale.value,
    //         },
    //         title: pageMeta.title.value,
    //         meta: [
    //             {
    //                 name: 'description',
    //                 content: pageMeta.description.value,
    //             },
    //             {
    //                 name: 'robots',
    //                 content: robots.content.value,
    //             },
    //             ...pageMeta.meta.value,
    //         ],
    //     }
    // })

    // head.addHeadObjs(headObject)

    const releasedRoutes: string[] = [
        ROUTE_HOME,
        ROUTE_PROFILE_MY_ORDERS,
        ROUTE_READY_TO_GO_RINGS,
        ROUTE_READY_TO_GO_RING,
        ROUTE_JEWELLERIES,
        ROUTE_JEWELLERY,
        ROUTE_WEDDING_RINGS_HOME,
        ROUTE_WEDDING_RINGS,
        ROUTE_WEDDING_RING,
        ROUTE_QUESTIONNAIRE,
        ROUTE_ENGAGEMENT_RING,
        ROUTE_ENGAGEMENT_RINGS,
        ROUTE_ENGAGEMENT_RING_GEMSTONE,
        ROUTE_ENGAGEMENT_RINGS_HOME,
        ROUTE_ENGAGEMENT_RING_LAB_GROWN,
        ROUTE_ENGAGEMENT_RING_STONE_RECOMMENDATIONS,
        ROUTE_NATURAL_DIAMOND_SEARCH,
        ROUTE_LAB_GROWN_DIAMOND_SEARCH,
        ROUTE_DTL_REPORT,
        ROUTE_NON_SKU_LAB_GROWN_DIAMOND_SEARCH,
        ROUTE_GEMSTONE_SEARCH,
        ROUTE_BLOG,
        ROUTE_BLOG_POST,
        ROUTE_BLOG_CATEGORY,
        ROUTE_BLOG_TAG,
        ROUTE_BLOG_AUTHOR,
    ]

    const releasedPages: RegExp[] = [
        /\/custom-engagement-rings/,
        /\/profile\/my-orders/,
        /\/ready-to-go(\/.*)?$/,
        /\/jewellery(\/.*)?$/,
        /\/wedding-rings(\/.*)?$/,
        /\/engagement-rings(\/.*)?$/,
        /\/blog(\/.*)?$/,
        /\/warranty-activation(\/.*)?$/,
        /\/diamonds(\/.*)?$/,
        /\/coloured-diamonds(\/.*)?$/,
        /\/gemstones(\/.*)?$/,
        /\/ring-guidance(\/.*)?$/,
        /\/diamond-guidance(\/.*)?$/,
        /\/about-us\/diamond-upgrade-policy/,
        /\/about-us\/our-guarantee/,
        /\/about-us\/our-guarantee\/insurance/,
        /\/about-us\/customer-reviews/,
        /\/about-us\/price-match-policy/,
        /\/about-us\/careers/,
        /\/about-us\/creative-studio/,
        /\/about-us\/as-seen-in/,
        /\/about-us$/,
        /\/collaborations/,
        /\/collaborations\/hermione-de-paula-moonrise-reef-collection-collaboration/,
        /\/ethical-engagement-rings/,
        /\/ring-size-guide/,
        /\/jewellery-collection/,
        /\/emerald-engagement-rings/,
        /\/organic-engagement-rings/,
        /\/vintage-engagement-rings/,
        /\/unique-engagement-rings/,
        /\/ten-most-popular-wedding-ring-designs/,
        /\/refer-a-friend/,
        /\/bespoke-engagement-rings/,
        /\/bespoke-engagement-rings-2/,
        /\/about-us\/payment-options/,
        /\/about-us\/annual-aftercare/,
        /\/about-us\/lifetime-manufacturer-warranty/,
        /\/about-us\/resizing-policy/,
        /\/about-us\/signature-ruby/,
        /\/about-us\/free-worldwide-shipping/,
        /\/about-us\/packaging/,
        /\/about-us\/faqs/,
        /\/about-us\/our-story/,
        /\/about-us\/the-ruby-club/,
        /\/about-us\/jobs/,
        /\/20-off-lg-diamonds/,
        /\/20-off-wedding-rings/,
        /\/visit-us/,
        /\/privacy/,
        /\/terms/,
        /\/cookies/,
        /\/returns-policy/,
        /\/proposal-rings/,
        /\/custom-engagement-ring-design/,
        /\/aftercare-service-request-thank-you/,
        /\/post-checkout-survey-thank-you/,
        /\/customer-love-stories/,
        /\/home-2/,
        /\/affiliates/,
        /\/lab-grown-diamond-engagement-ring-design/,
        /\/lab-grown-diamond-engagement-ring-designs/,
        /\/oval-engagement-ring-designs/,
        /\/marquise-engagement-ring-designs/,
        /\/sapphire-engagement-ring-designs/,
        /\/emerald-engagement-ring-designs/,
        /\/toi-et-moi-engagement-ring-designs/,
        /\/engagement-rings-near-me/,
        /\/lab-grown-diamond-engagement-rings/,
        /\/sapphire-engagement-rings/,
        /\/meet-the-makers/,
        /\/top-ten-engagement-rings/,
        /\/pink-diamond-engagement-rings/,
        /\/ethical-engagement-rings\/diamond-time-lapse/,
        /\/ethical-engagement-rings\/canada-mark-diamonds/,
        /\/collaborations/,
    ]

    const isReleasedUrl = installReleasedUrls(
        app,
        {
            releasedRoutes,
            releasedPages,
        },
        initialUrl
    )

    installRouterNavigation(app, router, isReleasedUrl)

    const { setAuth, user } = authPlugin

    const isBot = installIsBot(app, userAgent)
    installSegment(app, analyticsId, user, isBot)

    if (QA) {
        const getKey = (binding: DirectiveBinding): string => {
            const modifiers = Object.keys(binding.modifiers)

            if (modifiers.length) {
                return `data-qa-${modifiers.join('-')}`
            }

            return 'data-qa'
        }

        const handle = (el: HTMLElement, binding: DirectiveBinding): void => {
            el.setAttribute(
                getKey(binding),
                undefined === binding.value ? '' : binding.value
            )
            if (binding.arg) {
                el.setAttribute(`data-qa`, binding.arg)
            }
        }

        app.directive('qa', {
            created(el: HTMLElement, binding) {
                handle(el, binding)
            },
            updated(el, binding) {
                handle(el, binding)
            },
            getSSRProps(binding) {
                if (qaIsNoJs) {
                    return {
                        [getKey(binding)]: binding.value,
                    }
                }

                return undefined
            },
        })
    } else {
        app.directive('qa', {})
    }

    // eslint-disable-next-line vue-composable/composable-placement
    const amazonTraceId = useGtmVar('amazon-trace-id', '')

    const amazonTraceIdExchange = mapExchange({
        onResult(result) {
            const id = result.extensions?.['amazonTraceId']

            if (id) {
                amazonTraceId.value = id
            }

            return result
        },
    })

    const marketingCloudFormExchange = mapExchange({
        onResult(result) {
            const forms = result.extensions?.['marketingCloudForms'] as
                | string[]
                | undefined

            if (forms) {
                forms.forEach((item) => {
                    trackGtmEvent('WebsiteFormSubmit', {
                        FormSubmit: item,
                    })
                })
            }

            return result
        },
    })

    const authExchange = mapExchange({
        onError: (error) => {
            if (
                error.graphQLErrors.some(
                    (error) => error.extensions['category'] === 'authorization'
                )
            ) {
                setAuth(null)
            }
        },
    })

    const ignoredExtensions = ['validation', 'authorization']
    const sentryExchange = mapExchange({
        onError: async (error) => {
            const Sentry = await importSentry()

            let graphqlErrorLogFn: (error: GraphQLError) => void

            let networkErrorLogFn: (error: Error, response: any) => void

            if (Sentry) {
                graphqlErrorLogFn = (error: GraphQLError) => {
                    Sentry.captureMessage(error.message, (scope) => {
                        scope.setExtras(error.extensions)

                        return scope
                    })
                }
                networkErrorLogFn = (error, response) => {
                    Sentry.captureException(error, (scope) => {
                        if (response instanceof Response) {
                            scope.setExtra('response', {
                                status: response.status,
                                headers: response.headers,
                            })
                        } else {
                            scope.setExtra('response', response)
                        }

                        return scope
                    })
                }
            } else {
                if (!import.meta.env.SSR) {
                    return
                }
                graphqlErrorLogFn = (error: GraphQLError) => {
                    /* eslint-disable no-console */
                    console.error(error.message, error.extensions)
                }
                networkErrorLogFn = (error, response) => {
                    if (response instanceof Response) {
                        /* eslint-disable no-console */
                        console.error(
                            error,
                            'Status',
                            response.status,
                            'Headers',
                            response.headers
                        )
                    } else {
                        /* eslint-disable no-console */
                        console.error(error, 'Response', response)
                    }
                }
            }

            error.graphQLErrors.forEach((error) => {
                if (
                    ignoredExtensions.includes(
                        error.extensions['category'] as string
                    )
                ) {
                    return
                }
                graphqlErrorLogFn(error)
            })

            if (error.networkError) {
                networkErrorLogFn(error.networkError, error.response)
            }
        },
    })

    const abortController = new AbortController()
    installEventBus(app, abortController)
    installBench(app, abortController)
    installBenchAggregated(app)

    const ssrExchange = createSSRExchange({
        isClient: !import.meta.env.SSR,
        initialState: initialState.api,
        staleWhileRevalidate: false,
    })

    const acceptLanguageExchange = addHeaderExchange(() => ({
        'Accept-Language': locale.value.split('_')[0] ?? 'en',
    }))

    const createApiClient = () => {
        return createClient({
            url: urls.api,
            preferGetMethod: false, // true makes issues with Gems ORIGIN Header
            suspense: true,
            requestPolicy: 'cache-and-network',
            fetch: async (...args) => {
                if (abortController.signal.aborted) {
                    throw new Error('Query aborted by SSR')
                }

                const res = await fetch(...args)

                if (
                    res.status >= 400 &&
                    res.status < 500 &&
                    res.status !== 404
                ) {
                    console.error('Request error', res.status, await res.text())
                }

                return res
            },
            fetchOptions: {
                credentials: 'include',
                headers: {
                    'accept': 'application/json', // Fixes content not compressed by CloudFront,
                    'origin': initialUrl.hostname,
                },
                signal: abortController.signal,
            },
            exchanges: [
                // debugExchange,
                // devtoolsExchange,
                amazonTraceIdExchange,
                marketingCloudFormExchange,
                ...(sentryEnabled ? [sentryExchange] : []),
                authExchange,
                acceptLanguageExchange,
                ...exchanges,
                ssrExchange,
                persistedExchange({
                    preferGetForPersistedQueries: false, // WORKS ONLY WITH ABSOLUTE URLS
                }),
                retryExchange({
                    initialDelayMs: 25,
                    maxDelayMs: 1000,
                    maxNumberAttempts: 2,
                    retryIf: (err) =>
                        !!err.networkError ||
                        err.graphQLErrors.some((error) =>
                            isRateLimitError(error)
                        ),
                }),
                fetchExchange,
            ],
        })
    }

    const { createCookie } = cookies

    // This has to be before ANY requests to the API, so requests will pick the cookie
    const irclickid = initialUrl.searchParams.get('irclickid')
    if (irclickid) {
        createCookie(
            'irclickid',
            makeCookie(irclickid, {
                expires: new Date(
                    new Date().getTime() + 90 /* DAYS */ * 24 * 60 * 60 * 1000
                ),
                path: '/',
            })
        )
    }

    const { client: graphqlClient } = installGraphqlClient(app, createApiClient)

    install(app, graphqlClient)

    const ringBuilderDataStore = installRingBuilderDataStore(
        app,
        initialState.rb
    )
    installRingBuilderStore(app, cookieProvider)

    const foApp: FrontOfficeApp = {
        app,
        graphqlClient,
        router,
        head,
        i18n,
        locale,
        ssrExchange,
        absoluteUrlGenerator,
        isReleasedUrl,
        pinia,
        ringBuilderDataStore,
        routes,
        abortController,
        robots,
        initialUrl,
    }

    // required for init.spec.ts in Regression
    // also required in AppContent.vue to install init
    installApp(app)
    installFoApp(foApp)

    return foApp
}
